import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useCreateMarketingContact } from "@workspace/api-client-react";
import { Seo } from "@/components/Seo";
import { Section, PageHeader } from "@/components/Section";
import { trackEvent } from "@/lib/utils";
import { CheckCircle, ArrowLeft, Phone, Mail } from "lucide-react";

const VALID_TOPICS = ["sales", "support", "security", "other"] as const;
type Topic = (typeof VALID_TOPICS)[number];

function initialTopic(): Topic {
  if (typeof window === "undefined") return "sales";
  const t = new URLSearchParams(window.location.search).get("topic");
  return (VALID_TOPICS as readonly string[]).includes(t ?? "") ? (t as Topic) : "sales";
}

const ContactSchema = z.object({
  fullName: z.string().min(2, "Please enter your full name").max(120),
  email: z.string().email("Please enter a valid email address").max(200),
  agencyName: z.string().max(200).optional().or(z.literal("")),
  phone: z.string().max(40).optional().or(z.literal("")),
  message: z.string().min(10, "Tell us a little about what you need").max(4000),
  topic: z.enum(["sales", "support", "security", "other"]),
});

type ContactValues = z.infer<typeof ContactSchema>;

const TOPIC_SUCCESS: Record<Topic, { heading: string; blurb: string; eta: string }> = {
  sales: {
    heading: "We'll be in touch soon.",
    blurb: "A member of our sales team will reach out within one business day to learn more about your business and walk you through how FortifyID can work for you.",
    eta: "Reply within 1 business day",
  },
  support: {
    heading: "Support request received.",
    blurb: "Our support team will review your message and get back to you within one business day. If your issue is urgent, reply to the confirmation email and we'll prioritise it.",
    eta: "Reply within 1 business day",
  },
  security: {
    heading: "Security enquiry received.",
    blurb: "Our security team reviews every security enquiry carefully and will respond within two business days.",
    eta: "Reply within 2 business days",
  },
  other: {
    heading: "Message received.",
    blurb: "Thanks for getting in touch. We'll review your message and get back to you within one business day.",
    eta: "Reply within 1 business day",
  },
};

type SuccessState = { topic: Topic; email: string; name: string };

function SuccessScreen({ state, onReset }: { state: SuccessState; onReset: () => void }) {
  const { heading, blurb, eta } = TOPIC_SUCCESS[state.topic];
  return (
    <div className="mt-12 max-w-2xl">
      <div className="rounded-lg border border-[hsl(var(--brand-sand))] bg-[hsl(168,25%,97%)] p-8">
        <div className="flex items-start gap-4">
          <CheckCircle className="w-8 h-8 text-[hsl(var(--brand-forest))] shrink-0 mt-0.5" />
          <div>
            <h2 className="font-display text-xl font-semibold text-[hsl(var(--brand-charcoal))] mb-2">
              {heading}
            </h2>
            <p className="text-[hsl(var(--brand-charcoal))] text-sm leading-relaxed mb-4">
              {blurb}
            </p>
            <div className="flex flex-wrap items-center gap-4 text-sm text-[hsl(var(--brand-charcoal)/0.7)]">
              <span className="flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" />
                Confirmation sent to <strong className="text-[hsl(var(--brand-charcoal))]">{state.email}</strong>
              </span>
              <span className="flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5" />
                {eta}
              </span>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={onReset}
        className="mt-6 inline-flex items-center gap-2 text-sm text-[hsl(var(--brand-forest))] hover:underline"
      >
        <ArrowLeft className="w-4 h-4" />
        Send another message
      </button>
    </div>
  );
}

export default function Contact() {
  const [submitted, setSubmitted] = useState<SuccessState | null>(null);
  const mutation = useCreateMarketingContact();
  const form = useForm<ContactValues>({
    resolver: zodResolver(ContactSchema),
    defaultValues: { fullName: "", email: "", agencyName: "", phone: "", message: "", topic: initialTopic() },
  });

  const onSubmit = (values: ContactValues) => {
    const honeypot =
      (document.querySelector<HTMLInputElement>('input[name="website"]')?.value ?? "");
    mutation.mutate(
      {
        data: {
          fullName: values.fullName,
          email: values.email,
          agencyName: values.agencyName || null,
          phone: values.phone || null,
          message: values.message,
          topic: values.topic,
          website: honeypot || null,
        },
      },
      {
        onSuccess: () => {
          trackEvent("contact_submit", { topic: values.topic });
          setSubmitted({ topic: values.topic, email: values.email, name: values.fullName });
        },
        onError: () => {
          form.setError("root", { message: "We couldn't send that. Please try again in a moment." });
        },
      },
    );
  };

  return (
    <>
      <Seo
        title="Contact"
        description="Talk to the FortifyID team about a workspace, a security review, or a multi-team rollout."
        path="/contact"
      />
      <Section>
        <PageHeader
          eyebrow="Contact"
          title="Tell us about your business."
          lede="Sales, support, security review, network rollout — whatever you need. A real person reads every message."
        />

        {submitted ? (
          <SuccessScreen
            state={submitted}
            onReset={() => {
              setSubmitted(null);
              form.reset();
            }}
          />
        ) : (
          <div className="mt-12 max-w-2xl">
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5" data-testid="form-contact">
              {/* Honeypot — hidden from real users, must remain empty. */}
              <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
                <label>
                  Leave this field empty
                  <input type="text" name="website" tabIndex={-1} autoComplete="off" defaultValue="" />
                </label>
              </div>
              <Field label="Your name" error={form.formState.errors.fullName?.message}>
                <input
                  {...form.register("fullName")}
                  className="w-full rounded border border-[hsl(var(--brand-sand))] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand-forest))]"
                  data-testid="input-fullname"
                />
              </Field>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Work email" error={form.formState.errors.email?.message}>
                  <input
                    type="email"
                    {...form.register("email")}
                    className="w-full rounded border border-[hsl(var(--brand-sand))] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand-forest))]"
                    data-testid="input-email"
                  />
                </Field>
                <Field label="Phone (optional)">
                  <input
                    {...form.register("phone")}
                    className="w-full rounded border border-[hsl(var(--brand-sand))] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand-forest))]"
                    data-testid="input-phone"
                  />
                </Field>
              </div>
              <Field label="Business name (optional)">
                <input
                  {...form.register("agencyName")}
                  className="w-full rounded border border-[hsl(var(--brand-sand))] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand-forest))]"
                  data-testid="input-agency"
                />
              </Field>
              <Field label="What's this about?">
                <select
                  {...form.register("topic")}
                  className="w-full rounded border border-[hsl(var(--brand-sand))] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand-forest))]"
                  data-testid="select-topic"
                >
                  <option value="sales">Sales / a new workspace</option>
                  <option value="support">Support for an existing workspace</option>
                  <option value="security">Security review</option>
                  <option value="other">Something else</option>
                </select>
              </Field>
              <Field label="Message" error={form.formState.errors.message?.message}>
                <textarea
                  rows={6}
                  {...form.register("message")}
                  className="w-full rounded border border-[hsl(var(--brand-sand))] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand-forest))]"
                  data-testid="input-message"
                />
              </Field>
              {form.formState.errors.root && (
                <p className="text-sm text-[hsl(var(--destructive))]">{form.formState.errors.root.message}</p>
              )}
              <button
                type="submit"
                disabled={mutation.isPending}
                className="bg-[hsl(var(--brand-forest))] text-[hsl(var(--brand-bone))] px-6 py-3 rounded font-medium hover:bg-[hsl(168,25%,22%)] transition-colors disabled:opacity-60"
                data-testid="button-submit"
              >
                {mutation.isPending ? "Sending…" : "Send message"}
              </button>
            </form>
          </div>
        )}
      </Section>
    </>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-sm font-medium text-[hsl(var(--brand-charcoal))] mb-1.5">{label}</div>
      {children}
      {error && <div className="mt-1 text-xs text-[hsl(var(--destructive))]">{error}</div>}
    </label>
  );
}
